import { Lock, Check, ArrowLeft } from 'lucide-react';
import { GARAGE_SKINS, GARAGE_TRAILS } from '@/lib/focusDriveLevels';
import { equipFDSkin, equipFDTrail, playSfx } from '@/lib/loungeMeta';

/**
 * FocusDriveGarage — "Stripped Garage" for skins & trails.
 * 3 skin unlocks at L5/L10/L20 + 3 trail unlocks at the same milestones
 * (skin id=gold and trail id=none are always unlocked = the default set).
 */
export default function FocusDriveGarage({ fd, onBack, onChange }) {
  const skinsUnlocked = new Set(fd?.garage?.skinsUnlocked ?? ['gold']);
  const trailsUnlocked = new Set(fd?.garage?.trailsUnlocked ?? ['none']);
  const currentSkin = fd?.garage?.currentSkin ?? 'gold';
  const currentTrail = fd?.garage?.currentTrail ?? 'none';

  const equipSkin = (id) => { if (equipFDSkin(id)) { playSfx('button', { volume: 0.3 }); onChange?.(); } };
  const equipTrail = (id) => { if (equipFDTrail(id)) { playSfx('button', { volume: 0.3 }); onChange?.(); } };

  return (
    <div className="space-y-5" data-testid="fd-garage">
      <button type="button" onClick={onBack} data-testid="fd-garage-back"
              className="inline-flex items-center gap-2 text-xs text-navy/60 hover:text-navy transition-smooth">
        <ArrowLeft size={13}/> Back to level select
      </button>

      <div>
        <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">Stripped Garage</p>
        <h3 className="font-heading text-2xl text-navy mt-1">Skins</h3>
        <p className="text-navy/55 text-xs mt-1">Unlock a new look at levels 5, 10, and 20.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {GARAGE_SKINS.map(skin => {
          const unlocked = skinsUnlocked.has(skin.id);
          const equipped = currentSkin === skin.id;
          return (
            <button
              key={skin.id}
              type="button"
              disabled={!unlocked}
              onClick={() => equipSkin(skin.id)}
              data-testid={`fd-skin-${skin.id}`}
              className={`relative rounded-2xl p-4 text-left border transition-all ${
                !unlocked
                  ? 'bg-navy/5 border-navy/10 cursor-not-allowed'
                  : equipped
                    ? 'bg-navy border-gold shadow-lg'
                    : 'bg-white border-navy/10 hover:border-gold/40 hover:-translate-y-0.5'
              }`}
            >
              <div className="flex items-center gap-3">
                <SkinPreview color={skin.color} accent={skin.accent} muted={!unlocked}/>
                <div className="flex-1 min-w-0">
                  <p className={`font-heading text-base ${equipped ? 'text-white' : unlocked ? 'text-navy' : 'text-navy/40'}`}>
                    {skin.name}
                  </p>
                  <p className={`text-xs mt-0.5 ${equipped ? 'text-white/70' : unlocked ? 'text-navy/55' : 'text-navy/35'}`}>
                    {unlocked ? skin.desc : `Unlocks at level ${skin.unlockLevel}`}
                  </p>
                </div>
                {equipped && <Check size={14} className="text-gold" strokeWidth={2.5}/>}
                {!unlocked && <Lock size={13} className="text-navy/30"/>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <h3 className="font-heading text-2xl text-navy">Trails</h3>
        <p className="text-navy/55 text-xs mt-1">A quiet signature behind you.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {GARAGE_TRAILS.map(trail => {
          const unlocked = trailsUnlocked.has(trail.id);
          const equipped = currentTrail === trail.id;
          return (
            <button
              key={trail.id}
              type="button"
              disabled={!unlocked}
              onClick={() => equipTrail(trail.id)}
              data-testid={`fd-trail-${trail.id}`}
              className={`relative rounded-2xl p-4 text-left border transition-all ${
                !unlocked
                  ? 'bg-navy/5 border-navy/10 cursor-not-allowed'
                  : equipped
                    ? 'bg-navy border-gold shadow-lg'
                    : 'bg-white border-navy/10 hover:border-gold/40 hover:-translate-y-0.5'
              }`}
            >
              <div className="flex items-center gap-3">
                <TrailPreview color={trail.color} muted={!unlocked}/>
                <div className="flex-1 min-w-0">
                  <p className={`font-heading text-base ${equipped ? 'text-white' : unlocked ? 'text-navy' : 'text-navy/40'}`}>
                    {trail.name}
                  </p>
                  <p className={`text-xs mt-0.5 ${equipped ? 'text-white/70' : unlocked ? 'text-navy/55' : 'text-navy/35'}`}>
                    {unlocked ? trail.desc : `Unlocks at level ${trail.unlockLevel}`}
                  </p>
                </div>
                {equipped && <Check size={14} className="text-gold" strokeWidth={2.5}/>}
                {!unlocked && <Lock size={13} className="text-navy/30"/>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SkinPreview({ color, accent, muted }) {
  return (
    <div className="w-12 h-14 rounded-md relative shrink-0 shadow-inner"
         style={{ background: muted ? '#E5E7EB' : color, opacity: muted ? 0.5 : 1 }}>
      <span className="absolute inset-x-1 top-2 h-1 rounded" style={{ background: muted ? '#F3F4F6' : accent }}/>
      <span className="absolute inset-x-1 bottom-2 h-1 rounded" style={{ background: muted ? '#F3F4F6' : accent }}/>
    </div>
  );
}
function TrailPreview({ color, muted }) {
  return (
    <div className="w-12 h-14 rounded-md relative shrink-0 bg-[#0F1A2E] overflow-hidden">
      {color && color !== 'transparent' && (
        <>
          <span className="absolute left-1/2 -translate-x-1/2 top-1 w-1 rounded-full h-9"
                style={{ background: muted ? '#4B5563' : color, opacity: muted ? 0.4 : 0.9,
                         boxShadow: muted ? 'none' : `0 0 10px ${color}` }}/>
          <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-2 h-2 rounded-full"
                style={{ background: muted ? '#4B5563' : color, opacity: muted ? 0.4 : 1 }}/>
        </>
      )}
      {(!color || color === 'transparent') && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-2 w-2 h-2 rounded-full bg-white/30"/>
      )}
    </div>
  );
}
